import React, { useEffect, useState } from 'react';
import { Form, Input, Select, InputNumber, DatePicker, Switch, Button, Card, Row, Col, Alert, Divider } from 'antd';
import moment from 'moment';
import styled from 'styled-components';

const { Option } = Select;
const { TextArea } = Input;

const StyledCard = styled(Card)`
  max-width: 900px;
  margin: 0 auto;

  .ant-card-body {
    padding: 32px;
  }

  .ant-form-item-label > label {
    font-family: IBMPlexSans-SemiBold;
    font-size: 12px;
    font-weight: 600;
    color: rgb(79, 93, 109);
    text-transform: uppercase;
  }

  .form-section-title {
    font-family: IBMPlexSans-Bold;
    font-size: 14px;
    font-weight: 600;
    color: rgb(51, 63, 72);
    text-transform: uppercase;
    margin-bottom: 16px;
    margin-top: 24px;
  }

  .info-text {
    font-size: 12px;
    color: rgb(100, 116, 139);
    margin-bottom: 16px;
    line-height: 1.6;
  }

  .button-group {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 32px;
  }
`;

const InfoAlert = styled(Alert)`
  margin-bottom: 24px;

  .ant-alert-message {
    font-size: 13px;
  }
`;

const DiscountCodeForm = ({
  onSubmit,
  onCancel,
  loading = false,
  initialValues = {},
  venues = [],
  submitText = 'CREATE DISCOUNT CODE',
  showVenueSelector = true
}) => {
  const [form] = Form.useForm();
  const [discountType, setDiscountType] = useState(initialValues.discountType || 'percentage');

  useEffect(() => {
    // Reset form with initial values
    const formValues = {
      ...initialValues,
      validFrom: initialValues.validFrom ? moment(initialValues.validFrom) : null,
      validUntil: initialValues.validUntil ? moment(initialValues.validUntil) : null
    };
    form.setFieldsValue(formValues);
    if (initialValues.discountType) {
      setDiscountType(initialValues.discountType);
    }
  }, [initialValues, form]);

  const handleDiscountTypeChange = value => {
    setDiscountType(value);
    // Clear the other discount field when switching types
    if (value === 'percentage') {
      form.setFieldsValue({ discountAmount: null });
    } else {
      form.setFieldsValue({ discountPercentage: null });
    }
  };

  const handleSubmit = async values => {
    // Convert moment objects to ISO strings
    const submitValues = {
      ...values,
      code: values.code.toUpperCase(), // Ensure uppercase
      validFrom: values.validFrom ? values.validFrom.toISOString() : null,
      validUntil: values.validUntil ? values.validUntil.toISOString() : null,
      // Remove the field that shouldn't be sent based on discount type
      discountPercentage: values.discountType === 'percentage' ? values.discountPercentage : null,
      discountAmount: values.discountType === 'fixed_amount' ? values.discountAmount : null
    };

    await onSubmit(submitValues);
  };

  return (
    <StyledCard>
      <InfoAlert
        message="Discount Code Information"
        description="Discount codes are immutable once created (except for the active status). Make sure all details are correct before creating. Use uppercase letters, numbers, hyphens, or underscores for the code."
        type="info"
        showIcon
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          discountType: 'percentage',
          isActive: true
        }}>
        {/* Venue Selection */}
        {showVenueSelector && (
          <>
            <div className="form-section-title">Venue Assignment</div>
            <p className="info-text">
              Select which venue this discount code applies to. Discount codes are venue-specific and cannot be changed after creation.
            </p>
            <Form.Item label="Venue" name="venueId" rules={[{ required: true, message: 'Please select a venue' }]}>
              <Select
                placeholder="Select a venue"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                {venues.map(venue => (
                  <Option key={venue.id} value={venue.id}>
                    ID: {venue.id} - {venue.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Divider />
          </>
        )}

        {/* Basic Information */}
        <div className="form-section-title">Basic Information</div>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Discount Code"
              name="code"
              rules={[
                { required: true, message: 'Please enter a discount code' },
                {
                  pattern: /^[A-Z0-9_-]+$/i,
                  message: 'Only letters, numbers, hyphens, and underscores allowed'
                },
                { max: 50, message: 'Code must be 50 characters or less' }
              ]}
              tooltip="This will be converted to uppercase. Use a memorable code like SUMMER25 or EARLYBIRD">
              <Input
                placeholder="e.g., SUMMER25"
                onChange={e => {
                  const value = e.target.value.toUpperCase();
                  form.setFieldsValue({ code: value });
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Status" name="isActive" valuePropName="checked" tooltip="Inactive codes cannot be used by customers">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" defaultChecked />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Description (Internal)" name="description" tooltip="Internal notes about this discount code - not visible to customers">
          <TextArea rows={3} placeholder="e.g., Summer 2025 promotional campaign for email subscribers" />
        </Form.Item>

        <Divider />

        {/* Discount Configuration */}
        <div className="form-section-title">Discount Configuration</div>
        <p className="info-text">
          Choose between a percentage discount (e.g., 25% off) or a fixed amount discount (e.g., $50 off). You can use 100% discounts or fixed amounts greater
          than the order total.
        </p>
        {/* <InfoAlert
          message="Important: Platform Fees Still Apply"
          description="Even with a 100% discount, platform fees (service fees, payment processing fees, etc.) will still be charged to the customer. These fees depend on your venue's configuration but are always applied regardless of the discount amount."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        /> */}

        <Form.Item label="Discount Type" name="discountType" rules={[{ required: true, message: 'Please select a discount type' }]}>
          <Select onChange={handleDiscountTypeChange}>
            <Option value="percentage">Percentage Discount</Option>
            <Option value="fixed_amount">Fixed Amount Discount</Option>
          </Select>
        </Form.Item>

        <Row gutter={16}>
          {discountType === 'percentage' && (
            <Col span={12}>
              <Form.Item
                label="Discount Percentage"
                name="discountPercentage"
                rules={[
                  { required: true, message: 'Please enter a discount percentage' },
                  { type: 'number', min: 0.01, max: 100, message: 'Must be between 0.01 and 100' }
                ]}>
                <InputNumber style={{ width: '100%' }} min={0.01} max={100} step={0.01} precision={2} placeholder="e.g., 25.00" addonAfter="%" />
              </Form.Item>
            </Col>
          )}

          {discountType === 'fixed_amount' && (
            <Col span={12}>
              <Form.Item
                label="Discount Amount"
                name="discountAmount"
                rules={[
                  { required: true, message: 'Please enter a discount amount' },
                  { type: 'number', min: 0.01, message: 'Must be greater than 0' }
                ]}>
                <InputNumber style={{ width: '100%' }} min={0.01} step={1} precision={2} placeholder="e.g., 50.00" addonBefore="$" />
              </Form.Item>
            </Col>
          )}

          <Col span={12}>
            <Form.Item label="Minimum Order Amount" name="minOrderAmount" tooltip="Optional: Order must meet this minimum subtotal to use the code">
              <InputNumber style={{ width: '100%' }} min={0} step={1} precision={2} placeholder="e.g., 200.00 (optional)" addonBefore="$" />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        {/* Usage Limits */}
        <div className="form-section-title">Usage Limits</div>
        <p className="info-text">Set optional limits on how and when this discount code can be used.</p>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Maximum Uses" name="maxUses" tooltip="Optional: Limit total number of times this code can be used">
              <InputNumber style={{ width: '100%' }} min={1} step={1} placeholder="Unlimited" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Valid From" name="validFrom" tooltip="Optional: Code cannot be used before this date">
              <DatePicker style={{ width: '100%' }} showTime format="YYYY-MM-DD HH:mm" placeholder="Start date (optional)" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Valid Until" name="validUntil" tooltip="Optional: Code cannot be used after this date">
              <DatePicker style={{ width: '100%' }} showTime format="YYYY-MM-DD HH:mm" placeholder="End date (optional)" />
            </Form.Item>
          </Col>
        </Row>

        {/* Submit Buttons */}
        <div className="button-group">
          {onCancel && (
            <Button onClick={onCancel} disabled={loading}>
              CANCEL
            </Button>
          )}
          <Button type="primary" htmlType="submit" loading={loading}>
            {submitText}
          </Button>
        </div>
      </Form>
    </StyledCard>
  );
};

export default DiscountCodeForm;
